import { fetchServer } from "@/features/utils/fetch-server";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Session } from "../types";

// Send a message to a public session
export function useSendPublicMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      publicId,
      message,
    }: {
      publicId: string;
      message: string;
    }): Promise<Session> => {
      const response = await fetchServer(
        `/api/sessions/v1/public/${publicId}/send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ message }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      return response.json();
    },
    onSuccess: (data, variables) => {
      const queryKey = ["public-session", variables.publicId];
      // Show the message now, from the reply we already hold…
      queryClient.setQueryData(queryKey, data);
      // …and refetch, which cancels any read still in flight. On the first message the
      // thread's initial load starts in the same breath as this send (the conversation
      // is created by it, see use-session-handle.ts); when that load was answered before
      // the insert but arrived after this reply, it overwrote the cache with an empty
      // thread — the visitor's "bonjour" vanished, and with the websocket room joined
      // too late to hear it, nothing brought it back. The refetch also restores what
      // this reply lacks (the older halves of a transferred thread).
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}
